import { memo, useCallback, useEffect, useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { FormHint } from 'bloko/blocks/form';
import FormattedNumericInput from 'bloko/blocks/formattedNumericInput';
import { InputChangeHandler } from 'bloko/blocks/inputText';
import Text, { TextSize } from 'bloko/blocks/text';
import VSpacing from 'bloko/blocks/vSpacing';
import debounce from 'bloko/common/debounce';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';
import { format, formatToReactComponent } from 'bloko/common/trl';

import calculateDiscount from 'src/api/price/calculateDiscount';
import Currency from 'src/components/Currency';
import FormatMoney from 'src/components/FormatMoney';
import { useNotification } from 'src/components/Notifications/Provider';
import translation from 'src/components/translation';
import useOnOffState from 'src/hooks/useOnOffState';
import { useSelectorNonNullable } from 'src/hooks/useSelector';
import useToggleState from 'src/hooks/useToggleState';
import { AppDispatch } from 'src/models/appDispatch';

import TwoColumnsWrapper from 'src/components/Price/PriceCart/PriceCartFooter/TwoColumnsWrapper';

import styles from './price-cart-discount.less';

const DISCOUNT_REQUEST_TIMEOUT = 1000;

const convertToString = (value: number) => (value === 0 ? '' : value.toString());

const convertToNumber = (value: string) => parseFloat(value) || 0;

const TrlKeys = {
    withoutDiscount: 'employer.price.cart.total.withoutDiscount',
    inPercent: 'employer.price.cart.discount.inPercent',
    inAmount: 'employer.price.cart.discount.inAmount',
    notNumberError: 'formatted.numeric.input.error.notNumber',
    decimalLengthError: 'formatted.numeric.input.error.decimalLength',
    hint: 'employer.price.cart.discount.hint',
};

const PriceCartDiscount: TranslatedComponent = ({ trls }) => {
    const currency = useSelectorNonNullable(({ priceCart }) => priceCart.currency);
    const customerStatus = useSelectorNonNullable(({ priceData: { customerStatus } }) => customerStatus);
    const { discountInAmount, discountInPercent, totalPriceWithoutDiscount } = useSelectorNonNullable(
        ({ priceCart }) => priceCart
    );
    const dispatch: AppDispatch = useDispatch();

    const [discountValueInPercent, setDiscountValueInPercent] = useState(convertToString(discountInPercent));
    const [discountValueInAmount, setDiscountValueInAmount] = useState(convertToString(discountInAmount / 100));
    const [loading, startLoading, stopLoading] = useOnOffState(false);
    const [forceUpdate, makeForceUpdate] = useToggleState(false);
    const { addNotification } = useNotification();

    // Скидка также пересчитывается при добавлении/удалении новых продуктов
    useEffect(() => {
        setDiscountValueInPercent(convertToString(discountInPercent));
        setDiscountValueInAmount(convertToString(discountInAmount / 100));
    }, [discountInAmount, discountInPercent, forceUpdate]);

    const debouncedCalculateDiscount = useMemo(
        () =>
            debounce((inPercent: number, inAmount: number) => {
                startLoading();
                dispatch(calculateDiscount(inPercent, inAmount, addNotification))
                    .then(
                        () => {
                            // На случай, если выполнили пересчет с тем значением скидки, с которым считали последний раз.
                            // Например, было 10%, удалили 0 и вернули его назад до действия debounce.
                            // discountInAmount, discountInPercent без изменений (useEffect не сработает), а один из инпутов пустой
                            makeForceUpdate();
                        },
                        () => null
                    )
                    .finally(stopLoading);
            }, DISCOUNT_REQUEST_TIMEOUT),
        [addNotification, dispatch, makeForceUpdate, startLoading, stopLoading]
    );

    const onDiscountInPercentChange = useCallback<InputChangeHandler>(
        (newDiscount) => {
            setDiscountValueInPercent(newDiscount);
            setDiscountValueInAmount('');
            debouncedCalculateDiscount(convertToNumber(newDiscount), 0);
        },
        [debouncedCalculateDiscount]
    );
    const onDiscountInAmountChange = useCallback<InputChangeHandler>(
        (newDiscount) => {
            setDiscountValueInAmount(newDiscount);
            setDiscountValueInPercent('');
            debouncedCalculateDiscount(0, convertToNumber(newDiscount) * 100);
        },
        [debouncedCalculateDiscount]
    );

    return (
        <>
            <TwoColumnsWrapper>
                <Text Element="span">{trls[TrlKeys.withoutDiscount]}:</Text>
                <Text Element="span" data-qa="cart__total-gross-price">
                    <FormatMoney currency={currency} inCents={true}>
                        {totalPriceWithoutDiscount}
                    </FormatMoney>
                </Text>
            </TwoColumnsWrapper>
            <VSpacing base={5} />
            <TwoColumnsWrapper>
                <Text Element="span">{trls[TrlKeys.inPercent]}</Text>
                <Text Element="span">
                    {formatToReactComponent(trls[TrlKeys.inAmount], {
                        '{0}': <Currency value={currency} />,
                    })}
                </Text>
            </TwoColumnsWrapper>
            <VSpacing base={2} />
            <TwoColumnsWrapper>
                <FormattedNumericInput
                    maxLength={4}
                    decimalLength={2}
                    errors={{
                        decimalLength: format(trls[TrlKeys.decimalLengthError], {
                            '{0}': 2,
                        }),
                        notNumber: trls[TrlKeys.notNumberError],
                    }}
                    value={discountValueInPercent}
                    onChange={onDiscountInPercentChange}
                    disabled={loading}
                    data-qa="cart__discount-input-percent"
                />
                <span className={styles.divider}>
                    <Text Element="span" size={TextSize.Large}>
                        ⮂
                    </Text>
                </span>
                <FormattedNumericInput
                    decimalLength={2}
                    maxLength={11}
                    errors={{
                        decimalLength: format(trls[TrlKeys.decimalLengthError], {
                            '{0}': 2,
                        }),
                        notNumber: trls[TrlKeys.notNumberError],
                    }}
                    value={discountValueInAmount}
                    onChange={onDiscountInAmountChange}
                    disabled={loading}
                    data-qa="cart__discount-input-amount"
                />
            </TwoColumnsWrapper>
            <VSpacing base={1} />
            <FormHint>
                {format(trls[TrlKeys.hint], {
                    '{0}': customerStatus?.maxDiscountPercent || 0,
                })}
            </FormHint>
            <VSpacing base={5} />
        </>
    );
};

export default memo(translation(PriceCartDiscount));
